import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";

/**
 * A customizable input component with adornments and styling options.
 *
 * @param {Object} props - The component props
 * @param {string} props.name - The name attribute for the input field (required)
 * @param {string|number} props.value - The current value of the input (required)
 * @param {Function} props.onChange - Callback function when input value changes (required)
 * @param {string} [props.placeholder] - Placeholder text for the input
 * @param {string} [props.type="text"] - Input type (text, password, email, number, etc.)
 * @param {boolean} [props.disabled=false] - Whether the input is disabled
 * @param {boolean} [props.error=false] - Whether the input has an error state
 * @param {boolean} [props.required=false] - Whether the input is required
 * @param {React.ReactNode} [props.startAdornment] - Content to display at the start of the input
 * @param {React.ReactNode} [props.endAdornment] - Content to display at the end of the input
 * @param {boolean} [props.hideControls=false] - Whether to hide number input controls
 * @param {React.Ref} ref - Forwarded ref to the input element
 * @returns {React.ReactElement} The rendered input component
 */
const Input = forwardRef(
  ({ name, value, onChange, placeholder, type = "text", disabled = false, readOnly = false, error = false, required = false, startAdornment = null, endAdornment = null, hideControls = false, fullwidth = false, ...props }, ref) => {
    const classes = useStyles({ hideControls, hasStartAdornment: !!startAdornment, hasEndAdornment: !!endAdornment, fullwidth });

    return (
      <div className={classes.inputWrapper}>
        {startAdornment && <div className={classes.startAdornment}>{startAdornment}</div>}
        <input
          ref={ref} // Forwarding the ref here
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          className={clsx(classes.input, readOnly && classes.readOnly, error && classes.error)}
          {...props}
        />
        {endAdornment && <div className={classes.endAdornment}>{endAdornment}</div>}
      </div>
    );
  }
);

Input.displayName = "Input";

Input.propTypes = {
  /** The name attribute for the input field */
  name: PropTypes.string.isRequired,
  /** The current value of the input */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /** Callback function when input value changes */
  onChange: PropTypes.func.isRequired,
  /** Placeholder text for the input */
  placeholder: PropTypes.string,
  /** Input type (text, password, email, number, tel, url, etc.) */
  type: PropTypes.oneOf(["text", "password", "email", "number", "tel", "url", "search", "date", "time", "datetime-local", "month", "week", "color"]),
  /** Whether the input is disabled */
  disabled: PropTypes.bool,
  /** Whether the input is read-only (visible but not editable) */
  readOnly: PropTypes.bool,
  /** Whether the input has an error state (shows red border) */
  error: PropTypes.bool,
  /** Whether the input is required */
  required: PropTypes.bool,
  /** Content to display at the start of the input (icons, text, etc.) */
  startAdornment: PropTypes.node,
  /** Content to display at the end of the input (icons, buttons, etc.) */
  endAdornment: PropTypes.node,
  /** Whether to hide number input spinner controls */
  hideControls: PropTypes.bool,
  /** Whether the input takes 100% of available width */
  fullwidth: PropTypes.bool,
};

Input.defaultProps = {
  type: "text",
  disabled: false,
  readOnly: false,
  error: false,
  required: false,
  startAdornment: null,
  endAdornment: null,
  hideControls: false,
  fullwidth: false,
  placeholder: "",
};

const useStyles = makeStyles((theme) => ({
  inputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    width: ({ fullwidth }) => (fullwidth ? "100%" : "inherit"),
  },
  input: {
    height: "40px",
    width: "100%",
    backgroundColor: "transparent",
    border: "1px solid #DCDCDC",
    borderRadius: 4,
    outline: "none",
    padding: ({ hasStartAdornment, hasEndAdornment }) => {
      const leftPadding = hasStartAdornment ? "40px" : "12px";
      const rightPadding = hasEndAdornment ? "40px" : "12px";
      return `0px ${rightPadding} 0px ${leftPadding}`;
    },
    fontSize: "16px",
    color: "#0C0E29",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    transition: "border-color 0.2s ease",
    "&:focus-visible": {
      borderColor: "var(--color-primary)",
    },
    "&:hover": {
      cursor: "text",
      borderColor: "var(--color-primary)",
    },
    "&:focus": {
      borderColor: "var(--color-primary)",
    },
    "&:disabled": {
      backgroundColor: "#f5f5f5",
      cursor: "not-allowed",
    },
    "&[type='number']::-webkit-inner-spin-button, &[type='number']::-webkit-outer-spin-button": {
      display: ({ hideControls }) => (hideControls ? "none" : "inline-block"),
    },
    "&[type='number']": {
      MozAppearance: ({ hideControls }) => (hideControls ? "textfield" : "auto"),
    },
  },
  readOnly: {
    backgroundColor: "#f9f9f9",
    cursor: "default",
    "&:hover": {
      borderColor: "#DCDCDC",
      cursor: "default",
    },
    "&:focus": {
      borderColor: "#DCDCDC",
    },
    "&:focus-visible": {
      borderColor: "#DCDCDC",
    },
  },
  error: {
    borderColor: "#D84949 !important",
  },
  startAdornment: {
    position: "absolute",
    left: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    "& svg": {
      width: "20px",
      height: "20px",
    },
    "& path": {
      fill: "#0C0E29",
    },
  },
  endAdornment: {
    position: "absolute",
    right: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    "& svg": {
      width: "20px",
      height: "20px",
    },
    "& path": {
      fill: "#0C0E29",
    },
  },
}));

export default Input;
